
(() => {
  if (window.__fsPdpStoreCurrencyInit__) return;
  window.__fsPdpStoreCurrencyInit__ = true;

  let updateTimer = null;
  let cartRefreshTimer = null;
  let cartWatchToken = 0;
  let lastKnownCartSignature = null;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number === null) {
      return '0';
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      '$1' + thousands
    );
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  function formatMoney(cents, format) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }

    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const formatString = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    const placeholder = match ? match[1] : 'amount';
    let value = '';

    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  function parseJsonScript(root, selector, fallback) {
    try {
      const el = root.querySelector(selector);
      if (!el) return fallback;
      return JSON.parse(el.textContent || '');
    } catch (e) {
      return fallback;
    }
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function moneyMarkup(amountMinor, moneyFormat, key) {
    return `<span data-fs-money-wrap="${key}"><span class="money">${formatMoney(amountMinor, moneyFormat)}</span></span>`;
  }

  function moneyNodesReady(root) {
    const wraps = root.querySelectorAll('[data-fs-money-wrap]');
    if (!wraps.length) return true;

    for (const wrap of wraps) {
      const text = (wrap.textContent || '').trim();

      if (text.includes('<span') || text.includes('</span')) {
        return false;
      }

      if (wrap.querySelector('.glc-money')) {
        continue;
      }

      if (wrap.querySelector('.money')) {
        continue;
      }

      if (!/\d/.test(text)) {
        return false;
      }
    }

    return true;
  }

  function markRootsForSoftRefresh(root = document) {
    root.querySelectorAll('[data-fs-root]').forEach((el) => {
      if (el.dataset.fsHydrated === '1') {
        el.dataset.fsPendingRefresh = '1';
      }
    });
  }

  function queueReveal(root) {
    const forceRefresh = root.dataset.fsPendingRefresh === '1';

    if (root.dataset.fsHydrated === '1' && !forceRefresh) {
      root.dataset.jsReady = '1';
      return;
    }

    if (root._fsPdpRevealTimer) {
      clearTimeout(root._fsPdpRevealTimer);
    }

    root.dataset.jsReady = '0';

    let tries = 0;
    const maxTries = 40;
    const delay = 50;
    const startedAt = Date.now();
    const minWait = forceRefresh ? 220 : 120;

    function check() {
      tries += 1;

      const elapsed = Date.now() - startedAt;
      const ready = moneyNodesReady(root) && elapsed >= minWait;

      if (ready || tries >= maxTries) {
        root.dataset.jsReady = '1';
        root.dataset.fsHydrated = '1';
        root.dataset.fsPendingRefresh = '0';
        return;
      }

      root._fsPdpRevealTimer = setTimeout(check, delay);
    }

    check();
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart.js failed');
    return await res.json();
  }

  function getCartLevelDiscountMinor(cart) {
    if (!cart || !Array.isArray(cart.cart_level_discount_applications)) return 0;

    return cart.cart_level_discount_applications.reduce((sum, item) => {
      const value = parseInt(item.total_allocated_amount || 0, 10) || 0;
      return sum + value;
    }, 0);
  }

  function getCartSignature(cart) {
    const itemCount = parseInt((cart && cart.item_count) || 0, 10) || 0;
    const totalMinor =
      (parseInt((cart && cart.total_price) || 0, 10) || 0) + getCartLevelDiscountMinor(cart);

    return `${itemCount}:${totalMinor}`;
  }

  function queueCartRefresh(delay = 180) {
    markRootsForSoftRefresh(document);

    clearTimeout(cartRefreshTimer);
    cartRefreshTimer = setTimeout(() => {
      updateAll(document);
    }, delay);
  }

  function watchForCartChange(options = {}) {
    const token = ++cartWatchToken;
    const baseline = typeof options.baseline === 'string' ? options.baseline : lastKnownCartSignature;
    const startDelay = typeof options.startDelay === 'number' ? options.startDelay : 220;
    const interval = typeof options.interval === 'number' ? options.interval : 180;
    const maxAttempts = typeof options.maxAttempts === 'number' ? options.maxAttempts : 10;
    const refreshDelay = typeof options.refreshDelay === 'number' ? options.refreshDelay : 180;

    markRootsForSoftRefresh(document);
    clearTimeout(cartRefreshTimer);

    const poll = async (attempt) => {
      if (token !== cartWatchToken) return;

      try {
        const cart = await fetchCart();
        const signature = getCartSignature(cart);
        lastKnownCartSignature = signature;

        if (!baseline || signature !== baseline || attempt >= maxAttempts) {
          queueCartRefresh(refreshDelay);
          return;
        }
      } catch (e) {
        if (attempt >= maxAttempts) {
          queueCartRefresh(refreshDelay);
          return;
        }
      }

      setTimeout(() => poll(attempt + 1), interval);
    };

    setTimeout(() => poll(1), startDelay);
  }

  function getSelectedVariantAndQty(root, productData, variantPricesData) {
    const sectionScope = root.closest('section') || document;
    const form =
      sectionScope.querySelector('form[action*="/cart/add"]') ||
      root.closest('form') ||
      document.querySelector('form[action*="/cart/add"]');

    let variantId = null;
    let qty = 1;

    if (form) {
      const idInput = form.querySelector('[name="id"]');
      if (idInput) variantId = idInput.value;

      const qtyInput = form.querySelector('[name="quantity"]');
      if (qtyInput) {
        const q = parseInt(qtyInput.value || '1', 10);
        qty = isNaN(q) || q <= 0 ? 1 : q;
      }
    }

    let variantPriceMinor = 0;
    let variantEffectiveMinor = 0;

    if (variantId && Array.isArray(variantPricesData)) {
      const payload = variantPricesData.find((item) => String(item.id) === String(variantId));
      if (payload) {
        variantPriceMinor = parseInt(payload.price || 0, 10) || 0;
        variantEffectiveMinor = parseInt(payload.effective_price || 0, 10) || 0;
      }
    }

    if (!variantPriceMinor && productData && Array.isArray(productData.variants) && productData.variants.length) {
      const fallbackVariant =
        productData.variants.find((item) => String(item.id) === String(variantId)) ||
        productData.variants[0];

      if (fallbackVariant) {
        variantPriceMinor = parseInt(fallbackVariant.price || 0, 10) || 0;
      }
    }

    if (!variantEffectiveMinor && Array.isArray(variantPricesData) && variantPricesData.length) {
      const fallbackEffective =
        variantPricesData.find((item) => String(item.id) === String(variantId)) ||
        variantPricesData[0];

      if (fallbackEffective) {
        variantEffectiveMinor = parseInt(fallbackEffective.effective_price || 0, 10) || 0;
      }
    }

    return {
      qty,
      variantId,
      variantPriceMinor,
      variantEffectiveMinor
    };
  }

  function renderState(root, state) {
    const thresholdMinor = state.thresholdMinor;
    const cartTotalMinor = state.cartTotalMinor;
    const addMinor = state.addMinor;
    const moneyFormat = state.moneyFormat;
    const i18n = state.i18n;

    const notice = root.querySelector('[data-fs-notice]');
    const success = root.querySelector('[data-fs-success]');
    const fill = root.querySelector('[data-fs-fill]');
    const bar = root.querySelector('.fs-pdp__bar');
    const heading = root.querySelector('[data-fs-heading]');

    if (root.dataset.fsPendingRefresh === '1') {
      root.dataset.jsReady = '0';
    }

    const progress = thresholdMinor > 0
      ? clamp((cartTotalMinor / thresholdMinor) * 100, 0, 100)
      : 100;

    if (fill) fill.style.width = progress + '%';
    if (bar) bar.setAttribute('aria-valuenow', String(Math.round(progress)));

    if (cartTotalMinor >= thresholdMinor) {
      if (notice) notice.style.display = 'none';
      if (success) success.style.display = '';
      if (heading) heading.style.display = 'none';

      queueReveal(root);
      return;
    }

    if (notice) notice.style.display = '';
    if (success) success.style.display = 'none';
    if (heading) heading.style.display = '';

    const leftNow = Math.max(thresholdMinor - cartTotalMinor, 0);
    const afterTotal = cartTotalMinor + addMinor;

    let html = '';

    if (afterTotal >= thresholdMinor) {
      html = String(i18n.reach || '')
        .replace(/\{left_now\}/g, moneyMarkup(leftNow, moneyFormat, 'left-now'));
    } else {
      const leftAfter = Math.max(thresholdMinor - afterTotal, 0);

      html = String(i18n.not_reach || '')
        .replace(/\{left_now\}/g, moneyMarkup(leftNow, moneyFormat, 'left-now'))
        .replace(/\{left_after\}/g, moneyMarkup(leftAfter, moneyFormat, 'left-after'));
    }

    if (notice) {
      notice.innerHTML = html;
    }

    queueReveal(root);
  }

  async function updateOne(root) {
    const thresholdMinor = parseInt(root.dataset.thresholdShopMinor || '0', 10) || 0;
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';

    if (!thresholdMinor) {
      root.style.display = 'none';
      root.dataset.jsReady = '1';
      return;
    }

    root.style.display = '';

    const i18n = parseJsonScript(root, '[data-fs-i18n]', {
      available: 'Безкоштовна доставка вже доступна.',
      reach: 'До безкоштовної доставки залишилось <b>{left_now}</b>, додайте в кошик і <b>Ви отримаєте її</b>.',
      not_reach: 'До безкоштовної доставки залишилось <b>{left_now}</b>, додайте в кошик і залишиться <b>{left_after}</b>.'
    });

    const productData = parseJsonScript(root, '[data-fs-product-json]', {});
    const variantPricesData = parseJsonScript(root, '[data-fs-variant-prices-json]', []);

    const cart = await fetchCart();
    lastKnownCartSignature = getCartSignature(cart);

    const cartTotalMinor =
      (parseInt((cart && cart.total_price) || 0, 10) || 0) + getCartLevelDiscountMinor(cart);

    const selected = getSelectedVariantAndQty(root, productData, variantPricesData);
    const addMinor = (selected.variantEffectiveMinor || selected.variantPriceMinor || 0) * (selected.qty || 1);

    renderState(root, {
      thresholdMinor,
      cartTotalMinor,
      addMinor,
      moneyFormat,
      i18n
    });
  }

  function updateAll(root = document) {
    root.querySelectorAll('[data-fs-root]').forEach((el) => {
      updateOne(el).catch(() => {
        el.dataset.jsReady = '1';
        el.dataset.fsHydrated = '1';
        el.dataset.fsPendingRefresh = '0';
      });
    });
  }

  function scheduleUpdate(root = document, delay = 120, options = {}) {
    if (options.softRefresh) {
      markRootsForSoftRefresh(root);
    }

    clearTimeout(updateTimer);
    updateTimer = setTimeout(() => updateAll(root), delay);
  }

  function bindRoot(root) {
    if (!root || root.dataset.fsBound === '1') return;
    root.dataset.fsBound = '1';

    const sectionScope = root.closest('section') || document;
    const form =
      sectionScope.querySelector('form[action*="/cart/add"]') ||
      root.closest('form') ||
      document.querySelector('form[action*="/cart/add"]');

    if (form) {
      form.addEventListener('change', (e) => {
        const el = e.target;
        if (!el) return;

        if (el.name === 'id' || el.name === 'quantity') {
          scheduleUpdate(document, 100, { softRefresh: true });
        }
      });

      form.addEventListener('input', (e) => {
        const el = e.target;
        if (el && el.name === 'quantity') {
          scheduleUpdate(document, 100, { softRefresh: true });
        }
      });

      form.addEventListener('submit', () => {
        watchForCartChange({
          baseline: lastKnownCartSignature,
          startDelay: 260,
          interval: 180,
          maxAttempts: 12,
          refreshDelay: 180
        });
      });
    }
  }

  function bindThemeCartEvents() {
    const ajaxCartForm = document.getElementById('AjaxCartForm');
    if (!ajaxCartForm || ajaxCartForm.dataset.fsCartEventBound === '1') return;

    ajaxCartForm.dataset.fsCartEventBound = '1';

    ajaxCartForm.addEventListener('cart-updated', function () {
      queueCartRefresh(180);
    });

    ajaxCartForm.addEventListener('change', function (e) {
      const el = e.target;
      if (!el) return;

      if (
        el.matches('.qty') ||
        el.matches('[name="updates[]"]')
      ) {
        queueCartRefresh(180);
      }
    });

    ajaxCartForm.addEventListener('click', function (e) {
      const el = e.target;
      if (!el || !el.closest) return;

      if (el.closest('.remove')) {
        queueCartRefresh(200);
      }
    });
  }

  function bindRefreshCartHook() {
    if (typeof window.refreshCart !== 'function' || window.refreshCart.__fsWrapped === true) return;

    const originalRefreshCart = window.refreshCart;

    const wrappedRefreshCart = function () {
      const baseline = lastKnownCartSignature;
      const result = originalRefreshCart.apply(this, arguments);

      watchForCartChange({
        baseline,
        startDelay: 260,
        interval: 180,
        maxAttempts: 12,
        refreshDelay: 180
      });

      return result;
    };

    wrappedRefreshCart.__fsWrapped = true;
    window.refreshCart = wrappedRefreshCart;
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-fs-root]').forEach((el) => {
      el.dataset.jsReady = el.dataset.fsHydrated === '1' ? '1' : '0';
      bindRoot(el);
    });

    bindThemeCartEvents();
    bindRefreshCartHook();
    updateAll(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  window.addEventListener('load', () => {
    bindThemeCartEvents();
    bindRefreshCartHook();
    scheduleUpdate(document, 140);
  }, { once: true });

  const mo = new MutationObserver((muts) => {
    let shouldRefresh = false;

    for (const m of muts) {
      if (!m.addedNodes) continue;

      m.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return;

        if (n.matches?.('[data-fs-root]')) {
          if (!n.dataset.fsHydrated) n.dataset.jsReady = '0';
          bindRoot(n);
          shouldRefresh = true;
        }

        n.querySelectorAll?.('[data-fs-root]').forEach((el) => {
          if (!el.dataset.fsHydrated) el.dataset.jsReady = '0';
          bindRoot(el);
          shouldRefresh = true;
        });

        if (
          n.id === 'AjaxCartForm' ||
          n.querySelector?.('#AjaxCartForm') ||
          n.id === 'AjaxCartSubtotal' ||
          n.querySelector?.('#AjaxCartSubtotal')
        ) {
          shouldRefresh = true;
        }
      });
    }

    if (shouldRefresh) {
      bindThemeCartEvents();
      bindRefreshCartHook();
      queueCartRefresh(180);
    }
  });

  mo.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  document.addEventListener('shopify:section:load', () => {
    initAll(document);
    bindThemeCartEvents();
    bindRefreshCartHook();
    scheduleUpdate(document, 140);
  });

  document.addEventListener('variant:change', () => scheduleUpdate(document, 100, { softRefresh: true }));
  document.addEventListener('currency:change', () => queueCartRefresh(220));
  document.addEventListener('shopify:currency:change', () => queueCartRefresh(220));
  document.addEventListener('cart-updated', () => queueCartRefresh(180));
  document.addEventListener('cart:updated', () => queueCartRefresh(180));
  document.addEventListener('cart:refresh', () => queueCartRefresh(180));
  document.addEventListener('cart:change', () => queueCartRefresh(180));
  document.addEventListener('ajaxProduct:added', () => {
    watchForCartChange({
      baseline: lastKnownCartSignature,
      startDelay: 180,
      interval: 160,
      maxAttempts: 12,
      refreshDelay: 180
    });
  });
  document.addEventListener('product:added', () => {
    watchForCartChange({
      baseline: lastKnownCartSignature,
      startDelay: 180,
      interval: 160,
      maxAttempts: 12,
      refreshDelay: 180
    });
  });

  function isCartRemoveElement(el) {
    if (!el || !el.closest) return false;

    return !!el.closest([
      '.quick-cart [data-remove-item]',
      '.quick-cart .quick-cart__item-remove',
      '.cart-drawer [data-remove-item]',
      '.cart-drawer .quick-cart__item-remove',
      '#AjaxCartForm [data-remove-item]',
      '#AjaxCartForm .quick-cart__item-remove',
      '[data-cart-remove]',
      '[data-remove-from-cart]',
      '[data-cart-item-remove]',
      '.cart-item__remove',
      '.drawer-cart__remove',
      '.mini-cart__remove'
    ].join(','));
  }

  document.addEventListener('click', function (e) {
    if (!isCartRemoveElement(e.target)) return;
    queueCartRefresh(200);
  }, { passive: true });
})();
